//@flow
import { loadStripe } from '@stripe/stripe-js'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Meteor } from 'meteor/meteor'
import { toast } from 'react-toastify'

import { makeStyles } from '@material-ui/styles'
import { Typography } from '@material-ui/core'
import Link from '@material-ui/core/Link'
import STRIPE_KEY from '../constants/STRIPE_KEY'
import useActualOffer from '../hooks/useActualOffer'
import isSubscription from '../lib/subscriptions/isSubscription'

import Button from './Button'
import amex from '../assets/icons/amex.png'
import mastercard from '../assets/icons/mastercard.png'
import visa from '../assets/icons/visa.png'
import sepa from '../assets/icons/sepa.png'
import { dispatch, useGlobalState } from '../store'
import MainLoader from './loadings/MainLoader'
import PaymentOption, {
  clientSecretStripe,
  elementsInstance,
  stripeInstance,
} from './PaymentOption'
import CheckSimple from './CheckSimple'
import { formattedMessage } from '../CurrentLocale'
import type { PaymentOptionType } from './PaymentOption'
import { CONTRACT_PAYMENT_TYPE } from '../schema/contracts'
import { openAlertDialog } from '../fonction'
import askPreauthCB from '../lib/askPreAuthCB'
import { PAYMENT_LOADING_FALSE, PAYMENT_LOADING_TRUE } from '../reducers/payment'
import matchUrl from '../lib/matchUrl'
import type { GlobalState } from '../types/globalState'

type PaymentDetailsType = {
  cardNumber: string,
  cardExpirationDate: string,
  cardCvx: string,
  cardholderName: string,
  IBAN: string,
  BIC: string,
}

const useStyles = makeStyles((theme) => ({
  subTitle: {
    marginTop: 16,
    marginBottom: 16,
    fontWeight: 'bold',
  },
  spacer: {
    marginTop: '3vh',
  },
}))

export const paymentOptionsInit = [
  {
    id: 'ADD_IBAN',
    name: 'Prélèvement automatique',
    type: CONTRACT_PAYMENT_TYPE.SEPA,
    logos: [sepa],
    detailledView: true,
  },
  {
    id: 'ADD_CB',
    name: 'Carte de crédit',
    type: CONTRACT_PAYMENT_TYPE.CB,
    logos: [visa, mastercard, amex],
    detailledView: true,
  },
]

const PaymentSection = ({ onSubmit }) => {
  const [{ loading }]: GlobalState = useGlobalState('payment')
  const [theme]: GlobalState = useGlobalState('theme')

  const [cguAccepted, setCguAccepted] = useState<boolean>(false)
  const [selectedOption, setSelectedOption] = useState<{ id: '', type: '' }>({ id: '', type: '' })
  const [paymentOptions, setPaymentOptions] = useState<Array<PaymentOptionType>>(paymentOptionsInit)
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsType>({
    cardNumber: '',
    cardExpirationDate: '',
    cardCvx: '',
    cardholderName: '',
    IBAN: '',
    BIC: '',
  })
  const actualOffer = useActualOffer()

  const classes = useStyles()

  /**
   * @description Paramètre le moyen de paiement par défaut
   */
  useEffect(() => {
    const wrapperAsync = async () => {
      const paymentOptionsInserted = []
      const cards = await Meteor.promise('listCards')

      const bankAccounts = await Meteor.promise('listBankAccounts')

      cards.forEach((card) => {
        paymentOptionsInserted.push({
          id: card.id,
          CardId: card.id,
          name: `Carte de crédit ${card.alias}`,
          type: CONTRACT_PAYMENT_TYPE.CB,
          logos: [],
          detailledView: false,
        })
      })
      bankAccounts.forEach((bankAccount) => {
        paymentOptionsInserted.push({
          id: bankAccount.id,
          name: `SEPA se terminant par ${bankAccount.alias}`,
          type: CONTRACT_PAYMENT_TYPE.SEPA,
          logos: [],
          detailledView: false,
        })
      })

      setPaymentOptions(paymentOptionsInserted)
      setSelectedOption(paymentOptionsInserted?.[0] || null)
      dispatch({
        type: PAYMENT_LOADING_FALSE,
      })
    }
    dispatch({
      type: PAYMENT_LOADING_TRUE,
    })
    wrapperAsync()
  }, [])

  const globalValidation = async () => {
    if (!cguAccepted) {
      toast.error('Vous devez accepter les CGU avant de valider le paiement')
      return
    }

    dispatch({
      type: PAYMENT_LOADING_TRUE,
    })

    let result

    try {
      if (selectedOption.id === 'ADD_CB') {
        result = await stripeInstance.confirmCardSetup(clientSecretStripe, {
          payment_method: {
            card: elementsInstance.getElement('card'),
          },
        })
      } else if (selectedOption.id === 'ADD_IBAN') {
        const user = Meteor.user()

        result = await stripeInstance.confirmSepaDebitSetup(clientSecretStripe, {
          payment_method: {
            sepa_debit: elementsInstance.getElement('iban'),
            billing_details: {
              name: user.profile.name,
              email: user.emails?.[0]?.address || 'contact@callalawyer.fr',
              address: {
                line1: user.profile.address,
                country: 'fr',
              },
            },
          },
        })
      }

      if (selectedOption.id === 'ADD_CB' || selectedOption.id === 'ADD_IBAN') {
        const {
          setupIntent: { payment_method },
        } = result

        await handleNext({ ...selectedOption, id: payment_method })
      } else await handleNext(selectedOption)
    } catch (e) {
      console.error(e)
      toast.error(e.message || JSON.stringify(e))
    } finally {
      dispatch({
        type: PAYMENT_LOADING_FALSE,
      })
    }
  }

  const handleChange = (prop): Function => (value): any => {
    setPaymentDetails({ ...paymentDetails, [prop]: value })
  }

  const handleNext = async (_selectedOption) => {
    dispatch({
      type: PAYMENT_LOADING_TRUE,
    })

    try {
      // On sauvegarde le choix du client comme «paiement par défaut»
      await Meteor.promise('setPaymentTypePref', {
        id: _selectedOption.id,
      })

      const stripe = await loadStripe(STRIPE_KEY)

      if (_selectedOption.type === CONTRACT_PAYMENT_TYPE.CB && !isSubscription(actualOffer)) {
        // On est en mode CB + call unique, il faut une preauth
        const clientSecret = await askPreauthCB()

        const res = await stripe.confirmCardPayment(clientSecret)

        if (res.error) throw new Error(res.error.message)
      } else if (isSubscription(actualOffer)) {
        const { clientSecret } = await Meteor.promise('createSubscription')

        const res = await stripe.confirmCardPayment(clientSecret)
      }

      onSubmit({ payin: true, offerId: actualOffer._id })
    } catch (error) {
      console.error(error)

      openAlertDialog({
        message: error.message,
        title: formattedMessage({ id: 'error' }),
      })
    } finally {
      dispatch({
        type: PAYMENT_LOADING_FALSE,
      })
    }
  }

  if (!actualOffer) return <MainLoader />

  return (
    <>
      <Typography className={classes.subTitle}>Moyens de paiement par défaut</Typography>
      {paymentOptions.length === 0 && (
        <Typography style={{ textAlign: 'center', width: '100%', fontWeight: 'bold' }}>
          Pas de moyen de paiement par défaut
        </Typography>
      )}
      {paymentOptions.map((option) => (
        <PaymentOption
          key={option.id}
          option={option}
          selectedOption={selectedOption}
          onClickRadio={setSelectedOption}
          optionsDetails={paymentDetails}
        />
      ))}
      <Typography className={classes.subTitle}>Ou ajoutez un moyen de paiement:</Typography>
      {paymentOptionsInit.map((option) => (
        <PaymentOption
          key={option.name}
          option={option}
          selectedOption={selectedOption}
          onClickRadio={setSelectedOption}
          handleOptionsDetailsChange={handleChange}
          optionsDetails={paymentDetails}
        />
      ))}

      <br />

      <CheckSimple
        checked={cguAccepted}
        label={
          <span style={{ color: '#222120' }}>
            J'accepte les{' '}
            <Link
              style={{ color: theme.colors.secondaryButtonBackground, textDecoration: 'none' }}
              target="_blank"
              rel="noopener"
              href={matchUrl('cgv', theme) || '#'}
            >
              Conditions Générales d'Utilisation et de Vente de Call A Lawyer
            </Link>
            .
          </span>
        }
        handleChange={setCguAccepted}
      />

      <br />
      <br />

      <Button onClick={globalValidation} color="secondary" loading={loading} fullWidth>
        Payer {actualOffer.price} €
      </Button>

      <div id={'payment-element'} />
    </>
  )
}

export default PaymentSection
